import * as babylon from "@babylonjs/core";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import type { MeasureReading } from "../../DataServer/worker/pointCloud/packedPointCloud";
import { DEFAULT_POINT_SIZE } from "../../ModelLoader";
import type { PointCloudFrame } from "../components";

import { hslBlend } from "./gradient";

const COLOR_KIND = babylon.VertexBuffer.ColorKind;
const UV_KIND = babylon.VertexBuffer.UVKind;

/** The texels of a gradient's color strip, `colorLow` first. */
export const STRIP_LENGTH = 256;

/** The half of a point cloud's spec that decides how it is drawn, not what. */
export type PointCloudDisplay = Pick<
  schema.PointCloud,
  "color" | "colorMode" | "gradient" | "pointSize"
>;

/** How a frame is colored once the spec and the frame have both had their say. */
export type PointCloudColoring = "flat" | "rgb" | "gradient";

/**
 * One mesh for a streamed point cloud: point-list fill mode, unlit, with the
 * vertex buffers to be filled in by {@link uploadPointCloudFrame}.
 */
export function createPointCloudMesh(
  name: string,
  scene: babylon.Scene,
): { mesh: babylon.Mesh; material: babylon.StandardMaterial } {
  const mesh = new babylon.Mesh(name, scene);
  // No index buffer: Babylon skips rendering an indexed mesh with no indices,
  // and point fill mode draws with drawArrays over `subMesh.verticesCount`
  // when the mesh is unindexed.
  mesh.isUnIndexed = true;
  // The pick cache (`pickScenePoint`) assumes a mesh's vertices never change,
  // and a streamed cloud rewrites its vertices on every message.
  mesh.isPickable = false;
  const material = new babylon.StandardMaterial(`${name}.material`, scene);
  material.pointsCloud = true;
  material.pointSize = DEFAULT_POINT_SIZE;
  // Unlit: with no lighting term the output is the emissive — a color, plus
  // a texture sample when there is one — times the vertex color. A white
  // emissive shows the vertex colors, a colored one with no vertex colors
  // draws flat, and a black one under the gradient's strip shows the strip.
  material.disableLighting = true;
  material.emissiveColor = babylon.Color3.White();
  mesh.material = material;
  return { mesh, material };
}

/**
 * Replaces the mesh's vertices with the frame's and dresses them for
 * `display`, re-allocating the GPU buffers when the point count changes —
 * deliberate for the prototype; `updateVerticesData` for equal-sized frames is
 * a follow-up.
 *
 * The positions go through `applyToMesh`, which only sets the kinds the vertex
 * data has, so an attribute the frame is not drawn with is removed explicitly:
 * otherwise the previous frame's would linger, at the previous frame's vertex
 * count. The others are set after the positions, since setting positions
 * rebuilds the sub-mesh.
 */
export function uploadPointCloudFrame(
  mesh: babylon.Mesh,
  material: babylon.StandardMaterial,
  frame: PointCloudFrame,
  display: PointCloudDisplay,
): void {
  const vertexData = new babylon.VertexData();
  vertexData.positions = frame.positions;
  vertexData.applyToMesh(mesh, true);
  dress(mesh, material, frame, display);
}

/**
 * Applies a display-only change — point size, color mode, flat color,
 * gradient — to a mesh showing `frame`, without touching its positions.
 *
 * The frame is needed, and the component keeps it, because a change of mode
 * puts its colors or its gradient lookups on the mesh without a refetch.
 */
export function applyPointCloudDisplay(
  mesh: babylon.Mesh,
  material: babylon.StandardMaterial,
  frame: PointCloudFrame,
  display: PointCloudDisplay,
): void {
  dress(mesh, material, frame, display);
}

/**
 * Which coloring `display` resolves to for `frame`.
 *
 * "rgb" and the default "auto" draw the message's own colors when it carries
 * red/green/blue, and flat when it does not; "flat" never uses them;
 * "gradient" colors every message by its points' positions.
 */
export function pointCloudColoring(
  display: Pick<schema.PointCloud, "colorMode">,
  frame: Pick<PointCloudFrame, "colors">,
): PointCloudColoring {
  switch (display.colorMode ?? "auto") {
    case "flat":
      return "flat";
    case "gradient":
      return "gradient";
    case "auto":
    case "rgb":
      return frame.colors ? "rgb" : "flat";
  }
}

/** The per-point RGBA bytes a frame is drawn with, or undefined when it is not. */
export function pointCloudVertexColors(
  display: Pick<schema.PointCloud, "colorMode">,
  frame: Pick<PointCloudFrame, "colors">,
): Uint8Array | undefined {
  return pointCloudColoring(display, frame) === "rgb"
    ? frame.colors
    : undefined;
}

/**
 * Everything but the positions: the attributes the coloring needs on the
 * mesh, the material's emissive, and the point size.
 *
 * Each coloring owns one arrangement. Flat: no extra attribute, the flat color
 * as emissive. Rgb: the message's bytes as the color attribute, white
 * emissive. Gradient: the frame's lookups as the `uv` attribute, the
 * gradient's strip as the emissive texture over a black emissive color, and
 * the strip's transform placing the range (see
 * {@link pointCloudGradientTransform}). An attribute or texture another
 * arrangement left is removed, since Babylon would otherwise keep drawing
 * with it.
 */
function dress(
  mesh: babylon.Mesh,
  material: babylon.StandardMaterial,
  frame: PointCloudFrame,
  display: PointCloudDisplay,
): void {
  const coloring = pointCloudColoring(display, frame);
  material.pointSize = display.pointSize ?? DEFAULT_POINT_SIZE;
  setNormalizedAttribute(
    mesh,
    COLOR_KIND,
    coloring === "rgb" ? frame.colors : undefined,
  );
  setNormalizedAttribute(
    mesh,
    UV_KIND,
    coloring === "gradient" ? frame.measures : undefined,
  );
  if (coloring === "gradient") {
    const gradient = display.gradient ?? schema.DEFAULT_POINT_CLOUD_GRADIENT;
    const strip = gradientStrip(material, gradient);
    const { scale, offset } = pointCloudGradientTransform(
      frame.measureSpans[gradient.by],
      gradient,
    );
    // The height lookup is `u`, the distance lookup `v`; the strip lies along
    // whichever the gradient reads and is one texel across the other, which
    // the clamp makes irrelevant.
    if (gradient.by === "up") {
      strip.uScale = scale;
      strip.uOffset = offset;
      strip.vScale = 1;
      strip.vOffset = 0;
    } else {
      strip.vScale = scale;
      strip.vOffset = offset;
      strip.uScale = 1;
      strip.uOffset = 0;
    }
    material.emissiveColor = babylon.Color3.Black();
    return;
  }
  material.emissiveTexture?.dispose();
  material.emissiveTexture = null;
  material.emissiveColor =
    coloring === "rgb"
      ? babylon.Color3.White()
      : babylon.Color3.FromHexString(display.color);
}

/**
 * Gives the mesh `data` as the attribute `kind`, or removes the attribute.
 *
 * Declared normalized: `VertexData` and `setVerticesData` would infer the
 * integer type from the array but not that it is normalized, and the shader
 * would read 0..255 or 0..65535 rather than `[0, 1]`. Only a `VertexBuffer`
 * built with `normalized` carries that, so this is the one way integers reach
 * a point cloud mesh. Babylon keeps the array itself, not a copy: the frame in
 * the cache and the buffer the GPU was uploaded from are the same bytes.
 */
function setNormalizedAttribute(
  mesh: babylon.Mesh,
  kind: string,
  data: Uint8Array | Uint16Array | undefined,
): void {
  if (data) {
    // Already up: a display change re-dresses the mesh with the same frame,
    // and uploading its bytes again would only cost the copy.
    if (mesh.getVertexBuffer(kind)?.getData() === data) {
      return;
    }
    mesh.setVerticesBuffer(
      new babylon.VertexBuffer(mesh.getScene().getEngine(), data, kind, {
        updatable: true,
        size: kind === COLOR_KIND ? 4 : 2,
        type:
          data instanceof Uint8Array
            ? babylon.VertexBuffer.UNSIGNED_BYTE
            : babylon.VertexBuffer.UNSIGNED_SHORT,
        normalized: true,
      }),
    );
  } else if (mesh.isVerticesDataPresent(kind)) {
    mesh.removeVerticesData(kind);
  }
}

/**
 * The texels of `gradient`'s strip: `colorLow` at 0 through `colorHigh` at the
 * end, blended in HSL (`hslBlend`). RGBA bytes, opaque.
 */
export function gradientStripBytes(
  gradient: Pick<schema.PointCloudGradient, "colorLow" | "colorHigh">,
): Uint8Array {
  const blend = hslBlend(
    babylon.Color3.FromHexString(gradient.colorLow),
    babylon.Color3.FromHexString(gradient.colorHigh),
  );
  const color = new babylon.Color3();
  const bytes = new Uint8Array(STRIP_LENGTH * 4);
  // A clamped view rounds a channel to its nearest byte, and clamps a blend
  // that strays a hair outside [0, 1]; a plain byte view would wrap it.
  const clamped = new Uint8ClampedArray(bytes.buffer);
  for (let i = 0; i < STRIP_LENGTH; i++) {
    blend(i / (STRIP_LENGTH - 1), color);
    clamped[i * 4] = color.r * 255;
    clamped[i * 4 + 1] = color.g * 255;
    clamped[i * 4 + 2] = color.b * 255;
    clamped[i * 4 + 3] = 255;
  }
  return bytes;
}

/** What a strip on a material was drawn for, kept in the texture's metadata. */
interface StripMetadata {
  pointCloudStrip: Pick<
    schema.PointCloudGradient,
    "by" | "colorLow" | "colorHigh"
  >;
}

/**
 * The material's emissive texture as `gradient`'s strip: the one it has when
 * that is already this gradient's, redrawn in place when only the colors
 * differ, and made afresh when the measure does — a strip reads along `u`
 * for height and along `v` for distance, so those are textures of different
 * shapes. The texture is the material's to dispose.
 */
function gradientStrip(
  material: babylon.StandardMaterial,
  gradient: schema.PointCloudGradient,
): babylon.RawTexture {
  const current = material.emissiveTexture;
  const drawnFor = (current?.metadata as StripMetadata | undefined)
    ?.pointCloudStrip;
  if (current instanceof babylon.RawTexture && drawnFor) {
    if (
      drawnFor.colorLow === gradient.colorLow &&
      drawnFor.colorHigh === gradient.colorHigh &&
      drawnFor.by === gradient.by
    ) {
      return current;
    }
    if (drawnFor.by === gradient.by) {
      current.update(gradientStripBytes(gradient));
      current.metadata = stripMetadata(gradient);
      return current;
    }
  }
  current?.dispose();
  const [width, height] =
    gradient.by === "up" ? [STRIP_LENGTH, 1] : [1, STRIP_LENGTH];
  const strip = babylon.RawTexture.CreateRGBATexture(
    gradientStripBytes(gradient),
    width,
    height,
    material.getScene(),
    false,
    false,
    babylon.Texture.BILINEAR_SAMPLINGMODE,
  );
  // A lookup past either end reads that end's texel: the range's clamp.
  strip.wrapU = babylon.Texture.CLAMP_ADDRESSMODE;
  strip.wrapV = babylon.Texture.CLAMP_ADDRESSMODE;
  strip.metadata = stripMetadata(gradient);
  material.emissiveTexture = strip;
  return strip;
}

function stripMetadata(gradient: schema.PointCloudGradient): StripMetadata {
  return {
    pointCloudStrip: {
      by: gradient.by,
      colorLow: gradient.colorLow,
      colorHigh: gradient.colorHigh,
    },
  };
}

/**
 * Where on its strip a gradient reads each of a frame's lookups: Babylon
 * applies `lookup * scale + offset` before sampling, so this maps the frame's
 * `span`, what its lookups 0 and 1 stand for, onto the gradient's range, 0 at
 * the range's low end and 1 at its high end. A lookup past either end clamps
 * to that end's color.
 *
 * An end of the range the gradient leaves unpinned is the frame's own extreme
 * on that side, unless that meets or crosses the one end it pins: the frame
 * then lies wholly past the pinned end, and every point takes that end's
 * color, which a scale of 0 and an offset of 0 or 1 reads. A range with no
 * width reads the middle color throughout, as `gradientFraction` does.
 */
export function pointCloudGradientTransform(
  reading: MeasureReading,
  gradient: Pick<schema.PointCloudGradient, "min" | "max">,
): { scale: number; offset: number } {
  const { span, extremes } = reading;
  const pinnedEnd = pinnedEndReached(gradient, extremes.min, extremes.max);
  if (pinnedEnd !== undefined) {
    return { scale: 0, offset: pinnedEnd };
  }
  const min = gradient.min ?? extremes.min;
  const max = gradient.max ?? extremes.max;
  const width = max - min;
  if (!(width > 0)) {
    return { scale: 0, offset: 0.5 };
  }
  return {
    scale: (span.max - span.min) / width,
    offset: (span.min - min) / width,
  };
}

/**
 * Where on the gradient every point goes when only one end of the range is
 * pinned and the frame lies wholly at or past it — 0 at or below a pinned
 * minimum, 1 at or above a pinned maximum — or `undefined` when each point is
 * placed by its own value.
 *
 * The automatic end, the frame's own extreme on its side, then meets or crosses
 * the pinned one: a pinned minimum of 2 over a frame whose highest point is
 * 1.5. A range with no width reads as the middle color, so playback would
 * flicker between a gradient and one flat color as frames crossed the pinned
 * end. Every point is past that end, where the points past it in any other
 * frame clamp to its color, so it is that end's color they all take.
 */
function pinnedEndReached(
  gradient: Pick<schema.PointCloudGradient, "min" | "max">,
  lowest: number,
  highest: number,
): number | undefined {
  if (gradient.max === undefined && gradient.min !== undefined) {
    return highest <= gradient.min ? 0 : undefined;
  }
  if (gradient.min === undefined && gradient.max !== undefined) {
    return lowest >= gradient.max ? 1 : undefined;
  }
  return undefined;
}
